import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Plus, ImageIcon } from 'lucide-react';
import { Storage } from 'aws-amplify';
import useOrgData from '../../../application/hooks/useOrgData';
import {
  validateEmail,
  validatePhoneNumber,
} from '../../../application/routes/settings/utils/validation';
import { PageHeader } from '../../components/page-header';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { FormField } from '../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    id: string;
    imageName?: string;
    name: string;
  };
};

const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const LEGAL_ENTITY_TYPES = [
  'Limited Liability Company (LLC)',
  'Corporation (C-Corp)',
  'Corporation (S-Corp)',
  'Sole Proprietorship',
  'Partnership',
];

export function CompanyInformationPage() {
  const [org, setOrg] = useState<any>(null);
  const [hasChanged, setHasChanged] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    contactEmail?: string;
    contactPhone?: string;
    insuranceContactEmail?: string;
    insuranceContactPhone?: string;
  }>({});

  const { orgData, updateOrgData } = useOrgData();
  const toast = useToast();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (orgData) {
      setOrg(orgData);
      if (orgData.companyLogo) {
        setLogoPreview(
          `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
            orgData.companyLogo
          )}`
        );
      }
      if (orgData.coverPhoto) {
        setCoverPreview(
          `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
            orgData.coverPhoto
          )}`
        );
      }
    }
  }, [orgData]);

  useEffect(() => {
    if (org && orgData) {
      setHasChanged(JSON.stringify(org) !== JSON.stringify(orgData));
    }
  }, [org, orgData]);

  const updateField = useCallback((field: string, value: string) => {
    setOrg((prev: any) => ({ ...prev, [field]: value }));
    if (
      field === 'contactEmail' ||
      field === 'contactPhone' ||
      field === 'insuranceContactEmail' ||
      field === 'insuranceContactPhone'
    ) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }, []);

  const handleImageUpload = async (
    file: File,
    type: 'companyLogo' | 'coverPhoto'
  ) => {
    const formats = ['jpeg', 'jpg', 'png', 'gif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !formats.includes(fileExtension)) {
      toast.error('Image format must be PNG, JPG, or GIF');
      return;
    }

    const setUploading =
      type === 'companyLogo' ? setIsUploadingLogo : setIsUploadingCover;
    const setPreview =
      type === 'companyLogo' ? setLogoPreview : setCoverPreview;

    setUploading(true);
    try {
      // @ts-ignore
      const orgID = global?.me?.orgID;
      const storagePath = `company-info/${type}/${orgID}/${Date.now()}_${
        file.name
      }`;

      await Storage.put(storagePath, file, {
        contentType: file.type,
      });

      const cloudFrontUrl = `https://d22xeeaijfewl2.cloudfront.net/public/${encodeURIComponent(
        storagePath
      )}`;
      setPreview(cloudFrontUrl);
      setOrg((prev: any) => ({ ...prev, [type]: storagePath }));

      toast.success(
        `${
          type === 'companyLogo' ? 'Logo' : 'Cover photo'
        } uploaded successfully. Click Save Changes to persist.`
      );
    } catch (err) {
      console.error(`Error uploading ${type}:`, err);
      toast.error(
        `Failed to upload ${type === 'companyLogo' ? 'logo' : 'cover photo'}`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'companyLogo');
    }
    e.target.value = '';
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, 'coverPhoto');
    }
    e.target.value = '';
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (org?.contactEmail && !validateEmail(org.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address';
    }
    if (org?.contactPhone && !validatePhoneNumber(org.contactPhone)) {
      newErrors.contactPhone =
        'Please enter a valid phone number (at least 10 digits)';
    }
    if (
      org?.insuranceContactEmail &&
      !validateEmail(org.insuranceContactEmail)
    ) {
      newErrors.insuranceContactEmail = 'Please enter a valid email address';
    }
    if (
      org?.insuranceContactPhone &&
      !validatePhoneNumber(org.insuranceContactPhone)
    ) {
      newErrors.insuranceContactPhone =
        'Please enter a valid phone number (at least 10 digits)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix the errors before saving');
      return;
    }
    setIsSaving(true);
    try {
      await updateOrgData({
        id: org.id,
        companyLogo: org.companyLogo,
        coverPhoto: org.coverPhoto,
        legalBusinessName: org.legalBusinessName,
        dbaName: org.dbaName,
        legalEntityType: org.legalEntityType,
        einTaxId: org.einTaxId,
        statesOfOperation: org.statesOfOperation,
        contactAddress: org.contactAddress,
        contactCity: org.contactCity,
        contactState: org.contactState,
        contactZip: org.contactZip,
        contactPhone: org.contactPhone,
        contactEmail: org.contactEmail,
        insuranceContactName: org.insuranceContactName,
        insuranceContactTitle: org.insuranceContactTitle,
        insuranceContactPhone: org.insuranceContactPhone,
        insuranceContactEmail: org.insuranceContactEmail,
        licenseNumber: org.licenseNumber,
        licenseIssuingState: org.licenseIssuingState,
        licenseExpirationDate: org.licenseExpirationDate,
        defaultEstimateDisclaimer: org.defaultEstimateDisclaimer,
        standardScopeNotes: org.standardScopeNotes,
      });
      setHasChanged(false);
      toast.success('Company information updated successfully');
    } catch (err) {
      console.error('Error updating company information:', err);
      toast.error('Error updating company information');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (orgData) {
      setOrg(orgData);
    }
  };

  if (!org) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Company Information"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Company Information"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="max-w-[800px] mx-auto space-y-8">
          {/* Company Branding */}
          <div className="space-y-6">
            <h3 className="text-lg font-bold text-foreground">
              Company Branding
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company Logo Card */}
              <div className="border border-border rounded-xl p-6">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-base font-bold text-foreground">
                      Company Logo
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your company&apos;s logo image
                    </p>
                  </div>
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className="bg-muted rounded-3xl h-[309px] flex flex-col items-center justify-center gap-[30px] cursor-pointer hover:bg-muted/80 transition-colors overflow-hidden"
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    ) : logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Company Logo"
                        className="max-h-[250px] max-w-full object-contain"
                      />
                    ) : (
                      <>
                        <div className="flex flex-col items-center gap-2.5">
                          <ImageIcon className="size-6 text-foreground" />
                          <p className="text-base font-semibold text-foreground">
                            No logo uploaded yet
                          </p>
                        </div>
                        <div className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg">
                          <Plus className="size-6 text-foreground" />
                          <span className="text-base font-semibold text-foreground">
                            Upload Logo
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    className="hidden"
                    onChange={handleLogoSelect}
                  />
                </div>
              </div>

              {/* Cover Photo Card */}
              <div className="border border-border rounded-xl p-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-base font-bold text-foreground">
                      Cover Photo
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Your company&apos;s cover photo
                    </p>
                  </div>
                  <div
                    onClick={() => coverInputRef.current?.click()}
                    className="bg-muted rounded-3xl flex-1 min-h-[309px] flex flex-col items-center justify-center gap-[30px] cursor-pointer hover:bg-muted/80 transition-colors overflow-hidden"
                  >
                    {isUploadingCover ? (
                      <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    ) : coverPreview ? (
                      <img
                        src={coverPreview}
                        alt="Cover Photo"
                        className="max-h-[250px] max-w-full object-contain"
                      />
                    ) : (
                      <>
                        <div className="flex flex-col items-center gap-2.5">
                          <ImageIcon className="size-6 text-foreground" />
                          <p className="text-base font-semibold text-foreground">
                            No cover photo yet
                          </p>
                        </div>
                        <div className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg">
                          <Plus className="size-6 text-foreground" />
                          <span className="text-base font-semibold text-foreground">
                            Upload Photo
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    className="hidden"
                    onChange={handleCoverSelect}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Legal Identity */}
          <Section
            title="Legal Identity"
            subtitle="Your company's legal information"
          >
            <FormField label="Legal Business Name">
              <Input
                value={org.legalBusinessName || ''}
                onChange={(e) =>
                  updateField('legalBusinessName', e.target.value)
                }
              />
            </FormField>

            <FormField label="DBA Name (Optional)">
              <Input
                value={org.dbaName || ''}
                onChange={(e) => updateField('dbaName', e.target.value)}
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Legal Identity Type">
                <Select
                  value={org.legalEntityType || ''}
                  onValueChange={(val) => updateField('legalEntityType', val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEGAL_ENTITY_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="EIN / Tax ID">
                <Input
                  value={org.einTaxId || ''}
                  onChange={(e) => updateField('einTaxId', e.target.value)}
                />
              </FormField>
            </div>
          </Section>

          {/* Address */}
          <Section
            title="Address"
            subtitle="Your company's address information"
          >
            <FormField label="Address">
              <Input
                value={org.contactAddress || ''}
                onChange={(e) => updateField('contactAddress', e.target.value)}
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormField label="City">
                <Input
                  value={org.contactCity || ''}
                  onChange={(e) => updateField('contactCity', e.target.value)}
                />
              </FormField>

              <FormField label="State">
                <Select
                  value={org.contactState || ''}
                  onValueChange={(val) => updateField('contactState', val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Zip Code">
                <Input
                  value={org.contactZip || ''}
                  onChange={(e) => updateField('contactZip', e.target.value)}
                />
              </FormField>
            </div>
          </Section>

          {/* General Company Contact */}
          <Section
            title="General Company Contact"
            subtitle="Primary contact information for your company"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Company Phone Number"
                error={errors.contactPhone}
              >
                <Input
                  value={org.contactPhone || ''}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  className={errors.contactPhone ? 'border-destructive' : ''}
                />
              </FormField>

              <FormField label="Company Email" error={errors.contactEmail}>
                <Input
                  type="email"
                  value={org.contactEmail || ''}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  className={errors.contactEmail ? 'border-destructive' : ''}
                />
              </FormField>
            </div>
          </Section>

          {/* Insurance/Adjuster Contact Information */}
          <Section
            title="Insurance/Adjuster Contact Information"
            subtitle="Contact information that appears on insurance estimates"
          >
            <FormField label="Insurance Contact Name">
              <Input
                value={org.insuranceContactName || ''}
                onChange={(e) =>
                  updateField('insuranceContactName', e.target.value)
                }
              />
            </FormField>

            <FormField label="Insurance Contact Title / Role">
              <Input
                value={org.insuranceContactTitle || ''}
                onChange={(e) =>
                  updateField('insuranceContactTitle', e.target.value)
                }
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Insurance Contact Phone"
                error={errors.insuranceContactPhone}
              >
                <Input
                  value={org.insuranceContactPhone || ''}
                  onChange={(e) =>
                    updateField('insuranceContactPhone', e.target.value)
                  }
                  className={
                    errors.insuranceContactPhone ? 'border-destructive' : ''
                  }
                />
              </FormField>

              <FormField
                label="Insurance Contact Email"
                error={errors.insuranceContactEmail}
              >
                <Input
                  type="email"
                  value={org.insuranceContactEmail || ''}
                  onChange={(e) =>
                    updateField('insuranceContactEmail', e.target.value)
                  }
                  className={
                    errors.insuranceContactEmail ? 'border-destructive' : ''
                  }
                />
              </FormField>
            </div>
          </Section>

          {/* Licensing */}
          <Section
            title="Licensing"
            subtitle="Your company license information"
          >
            <FormField label="License Number">
              <Input
                value={org.licenseNumber || ''}
                onChange={(e) => updateField('licenseNumber', e.target.value)}
              />
            </FormField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="License Issuing State">
                <Select
                  value={org.licenseIssuingState || ''}
                  onValueChange={(val) =>
                    updateField('licenseIssuingState', val)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((state) => (
                      <SelectItem key={state} value={state}>
                        {state}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="License Expiration Date (Optional)">
                <Input
                  type="date"
                  value={org.licenseExpirationDate || ''}
                  onChange={(e) =>
                    updateField('licenseExpirationDate', e.target.value)
                  }
                />
              </FormField>
            </div>
          </Section>

          {/* Other Estimates Information */}
          <Section
            title="Other Estimates Information (Optional)"
            subtitle="Default text that appears on your estimates"
          >
            <FormField label="Default Estimate Disclaimer">
              <Input
                value={org.defaultEstimateDisclaimer || ''}
                onChange={(e) =>
                  updateField('defaultEstimateDisclaimer', e.target.value)
                }
                placeholder="Default Estimate Disclaimer"
              />
            </FormField>

            <FormField label="Standard Scope Notes">
              <Input
                value={org.standardScopeNotes || ''}
                onChange={(e) =>
                  updateField('standardScopeNotes', e.target.value)
                }
                placeholder="Standard Scope Notes"
              />
            </FormField>
          </Section>

          {/* Footer Buttons */}
          <div className="grid grid-cols-2 gap-4 pb-8">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={!hasChanged || isSaving}
              className="py-3 px-6 text-base font-bold rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanged || isSaving}
              className="py-3 px-6 text-base font-bold rounded-full"
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Section helper ──────────────────────────────────────────

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-foreground tracking-tight">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
